// Base export interface for an entity object, extended by classes in the entities directory
// -- Server only
// import {Dao} from "./dao";
import {EntityNames, EntityPlurals} from "@/entities";
import * as z from 'zod';

export const Status = {
    Create: 'C',
    Update: 'U',
    Delete: 'D'
};

export const ZUuid = z.uuidv4();
export type uuid = z.infer<typeof ZUuid>;

export type StatusType = typeof Status[keyof typeof Status];

export interface EntityBase {
    firmId: string,
    addedOn?: number | null,
    addedBy?: string | null,
    createdBy?: string | null,
    createdOn?: Date | null,
    dbAddedOn?: number | null,
    status: StatusType,
    entityId: string,
    entityType: string,
    importId?: string | null,
    importSource?: string | null,
    importedId?: string | null,
    shallow?: boolean | null,
    fresh?: boolean | null,
    persisted?: boolean | null,
    fatalError?: any | null,
    errorShown?: boolean | null,
    restore?: boolean | null,
    waitingToSave?: boolean | null,
    // OWA-786 (CXR-5-005): bounded-retry bookkeeping persisted on the durable
    // queue row, so reload or a new tab cannot grant a failing operation a
    // fresh attempt budget. Maintained by processQueue via idb.update and
    // preserved across same-key re-adds by idb.add. Never reaches the server:
    // entity constructors copy schema fields only, so ensure() drops these
    // from the POSTed object.
    retryAttempts?: number | null,
    nextRetryAt?: number | null,
    // Queue-only recovery marker for an earlier document version. Its payload
    // stays in IndexedDB after a newer version has been acknowledged.
    supersededByAddedOn?: number | null,
}

export interface Cloneable {
    clone(): any;
}

export type HasId = Partial<Cloneable> & {
    id?: string;
}

export interface Comparable<T> {
    compare(a: T, b: T): number;
}

export interface Sortable {
    sort(a?: Function): any;
}

export interface EntityType extends EntityBase, HasId, Comparable<any> {
    id: string;
    isTransientProperty(p: string): boolean;
    // OWA-554: every concrete entity implements clone() (it's on the Base class).
    // HasId inherits clone optionally via Partial<Cloneable>; redeclare it required
    // here so the type stops lying. Returns `any` to match Cloneable.clone(), so
    // call-site result types are unchanged — this only drops the bogus `| undefined`.
    clone(): any;
}

// -- Server Only
// export interface EntityMappingEntry {
//     name: string;
//     tableName: string;
//     plural: string;
//     clazz: EntityType;
//     dao: typeof Dao;
// }

export type EntityHistoryEntry<EntityName extends EntityNames, T extends EntityType> = {asOf: number} & Record<EntityName, T>;

export interface FieldRef {
    fieldName: string;
    type: string;
    isArray: boolean;
    refSchema: string;
}

export type SearchOptions = {
    caseSensitive?: boolean,
    matchAll?: boolean,
    includeSnippets?: boolean,
    matchReferences?: boolean,
    fuzzy?: boolean,
    distance?: number,
    wholeWords?: boolean
}

export type EntityMapper<T> = {
    name: EntityNames,
    plural: EntityPlurals,
    clazz: T,
    table: string,
    fetchOneQueue: Function,
    fetchOneSuccess: Function,
    fetchOneFailure: Function,
    fetchSuccess: Function,
    fetchFailure: Function,
    saveSuccess: Function,
    saveFailure: Function,
    saveManySuccess: Function,
    saveManyFailure: Function,
    removeSuccess: Function,
    removeFailure: Function,
    restoreSuccess: Function,
    restoreFailure: Function,
    save: Function,
    saveQueue: Function,
    saveMany: Function,
    saveManyQueue: Function,
    remove: Function,
    removeQueue: Function,
    restore: Function,
    restoreQueue: Function,
    restoreMany: Function,
    removeMany: Function,
    delete: Function
}
